import { FAKE, inject, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createSignal } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { ADD_TO_BASKET_API, AddProductRequest, AddToBasketApi, SelectedBasketOption } from './add-to-basket-api.js'
import { AddToBasketFailure, AddToBasketSuccess, parseAddToBasketResponse } from 'components/ordering/add-to-basket/basket-parser.js'
import { fetcher } from 'components/shared/fetcher.js'
import { ProductInBasketEntry, ProductsInBasket, parseProductsInBasket } from './basket-products-parser.js'


const [productsInBasket, setProductsInBasket] = createSignal<ProductsInBasket>({})
const [count, setCount] = createSignal<number>(0)


const BASKET_PRODUCTS_PATH = 'xhr/basket/basic/'

const JSON_HEADERS: HeadersInit = {
  'Accept': 'application/json',
}

const fetchProductsInBasket = async (): Promise<ProductsInBasket> => {
  const response = await fetcher.fetch(await createUrl(BASKET_PRODUCTS_PATH), { headers: JSON_HEADERS })

  if (!response.ok) throw new Error(`Request failed with status ${response.status}`)

  return parseProductsInBasket(await response.json())
}

const productInBasket = (productId: number): ProductInBasketEntry | undefined => {
  const lines = Object.values(productsInBasket()).filter(line => line.productId === productId)

  return lines.length === 1 ? lines[0] : undefined
}


export const BASKET_MUTATOR = Symbol('BASKET_MUTATOR')


export interface BasketMutator {
  initializeBasketMutator: (addToBasketPath: string, basketCount: number) => Promise<void>
  loadProductsInBasket: () => Promise<void>
  addToBasket: (productId: number, quantity: number, options?: SelectedBasketOption[]) => Promise<AddToBasketSuccess | AddToBasketFailure>
}


@registerDependency(REAL, BASKET_MUTATOR)
@registerDependency(FAKE, BASKET_MUTATOR)
export class RealAndFakeBasketMutator implements BasketMutator {

  @inject(ADD_TO_BASKET_API)
  private addToBasketApi!: AddToBasketApi

  private addToBasketPath: string | undefined

  private loadingProductsInBasket: Promise<void> | undefined

  initializeBasketMutator = (addToBasketPath: string, basketCount: number): Promise<void> => {
    if (this.loadingProductsInBasket) return this.loadingProductsInBasket

    this.addToBasketPath = addToBasketPath

    setCount(basketCount)
    setProductsInBasket({})

    this.loadingProductsInBasket = this.loadProductsInBasket()
    return this.loadingProductsInBasket
  }

  loadProductsInBasket = async (): Promise<void> => {
    setProductsInBasket(await fetchProductsInBasket())
  }

  addToBasket = async (product: number, quantity: number, options: SelectedBasketOption[] = []): Promise<AddToBasketSuccess | AddToBasketFailure> => {
    if (this.addToBasketPath === undefined) {
      throw new Error('BasketMutator not properly initialized')
    }

    const basketAddResponse = await this.addToBasketApi.add(
      new AddProductRequest(this.addToBasketPath, { product, quantity, options })
    )

    const parsedAddToBasketResponse = parseAddToBasketResponse(basketAddResponse)
    if (parsedAddToBasketResponse instanceof AddToBasketSuccess ) {
      this.upsertProductInBasket(parsedAddToBasketResponse.itemId, product, quantity, options)
      setCount(parsedAddToBasketResponse.basketCount)
    }

    return parsedAddToBasketResponse
  }

  private upsertProductInBasket = (itemId: number, productId: number, quantity: number, options: SelectedBasketOption[]) => {
    const line: ProductInBasketEntry = {
      itemId,
      productId,
      quantity,
      options: options.map(option => ({ id: option.id, quantity: option.quantity })),
    }

    setProductsInBasket(Object.assign({}, productsInBasket(), { [itemId]: line }))
  }
}


export const BASKET_STORE = Symbol('BASKET_STORE')


export interface BasketStore {
  productsInBasket: () => ProductsInBasket
  productInBasket: (productId: number) => ProductInBasketEntry | undefined
  count: () => number
}


@registerDependency(REAL, BASKET_STORE)
@registerDependency(FAKE, BASKET_STORE)
export class RealAndFakeBasketStore implements BasketStore {
  productsInBasket = productsInBasket
  productInBasket = productInBasket
  count = count
}
